import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	ArrowPathIcon,
	CheckCircleIcon,
	PlusIcon,
	TrashIcon,
} from "@heroicons/react/24/outline";
import { useSeatmapStore } from "../../../store/seatmapStore";

const OBJECT_LABELS = {
	seat: "좌석",
	blocked: "차단",
	toilet: "화장실",
	luggage: "짐칸",
	wheelchair_space: "휠체어",
	kiosk: "키오스크",
	aed: "AED",
	door: "출입문",
	vestibule: "통로",
	table: "테이블",
	partition: "칸막이",
	stage: "무대",
	wine_bar: "와인바",
	broadcast_room: "방송실",
};

const OBJECT_STYLES = {
	container:
		"w-16 h-12 bg-gray-100 border border-gray-300 rounded-md shadow-sm flex items-center justify-center text-[10px] text-gray-600",
	table:
		"w-16 h-12 bg-amber-100 border border-amber-300 rounded-md flex items-center justify-center text-[10px] text-amber-700",
	wheelchairForward:
		"w-16 h-12 bg-blue-50 border border-blue-300 rounded-md flex flex-col items-center justify-center text-[10px] text-blue-700",
	wheelchairBackward:
		"w-16 h-12 bg-rose-50 border border-rose-300 rounded-md flex flex-col items-center justify-center text-[10px] text-rose-700",
	stage:
		"w-16 h-12 bg-purple-100 border border-purple-400 rounded-md flex items-center justify-center text-[10px] text-purple-700 font-bold",
	wine_bar:
		"w-16 h-12 bg-red-100 border border-red-400 rounded-md flex items-center justify-center text-[10px] text-red-700 font-bold",
	broadcast_room:
		"w-16 h-12 bg-green-100 border border-green-400 rounded-md flex items-center justify-center text-[10px] text-green-700 font-bold",
};

const TOOL_OPTIONS = [
	{ id: "select", label: "선택", type: "action" },
	{ id: "erase", label: "지우개", type: "action" },
	{ id: "seat", label: "좌석" },
	{ id: "blocked", label: "차단" },
	{ id: "wheelchair_space", label: "휠체어" },
	{ id: "table", label: "테이블(셀)" },
	{ id: "partition", label: "칸막이(셀)" },
	{ id: "table_between", label: "테이블(행 사이)", type: "between" },
	{ id: "partition_between", label: "칸막이(행 사이)", type: "between" },
	{ id: "toilet", label: "화장실" },
	{ id: "luggage", label: "짐칸" },
	{ id: "door", label: "출입문" },
	{ id: "vestibule", label: "통로" },
	{ id: "kiosk", label: "키오스크" },
	{ id: "aed", label: "AED" },
	{ id: "stage", label: "무대" },
	{ id: "wine_bar", label: "와인바" },
	{ id: "broadcast_room", label: "방송실" },
];

const DIRECTION_ORDER = ["forward", "right", "backward", "left"];
const DIRECTION_LABELS = {
	forward: "↑ 순방향",
	right: "→ 오른쪽",
	backward: "↓ 역방향",
	left: "← 왼쪽",
};
const BETWEEN_TOOL_IDS = new Set(["table_between", "partition_between"]);
const OUTSIDE_TOOL_IDS = new Set([
	"toilet",
	"luggage",
	"door",
	"vestibule",
	"kiosk",
	"aed",
	"stage",
	"wine_bar",
	"broadcast_room",
]);

const DEFAULT_LAYOUT = {
	row_start: 1,
	row_end: 20,
	columns: ["A", "B", "C", "D"],
	seat_columns: ["A", "B", "C", "D"],
	aisle_after: 2,
	numbering: "alpha",
	orientation: "horizontal",
};

const makeEditorId = () =>
	`obj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const parseColumnsInput = (input) =>
	input
		.split(/[,\s]+/)
		.map((value) => value.trim())
		.filter(Boolean);

const resolveLayoutColumns = (layoutConfig, fallbackColumns) => {
	const columns =
		layoutConfig?.columns ||
		layoutConfig?.seat_columns ||
		fallbackColumns ||
		[];
	return Array.isArray(columns) && columns.length > 0 ? columns : null;
};

const resolveNumbering = (layoutConfig) => {
	const raw = String(layoutConfig?.numbering || layoutConfig?.seat_code_type || "")
		.toLowerCase()
		.trim();
	if (raw === "numeric" || raw === "alpha") {
		return raw;
	}
	return "alpha";
};

const resolveAisleAfter = (layoutConfig, columns) => {
	const raw =
		Number.isInteger(layoutConfig?.aisle_after)
			? layoutConfig.aisle_after
			: Number.isInteger(layoutConfig?.aisleAfter)
				? layoutConfig.aisleAfter
				: null;
	if (
		Number.isInteger(raw) &&
		raw > 0 &&
		Array.isArray(columns) &&
		raw < columns.length
	) {
		return raw;
	}
	return Math.floor((columns || []).length / 2);
};

const resolveRows = (layoutConfig, rowSet) => {
	const start = parseInt(layoutConfig?.row_start, 10);
	const end = parseInt(layoutConfig?.row_end, 10);
	if (Number.isInteger(start) && Number.isInteger(end) && start <= end) {
		return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
	}
	if (rowSet && rowSet.size > 0) {
		return Array.from(rowSet).sort((a, b) => a - b);
	}
	return Array.from({ length: 20 }, (_, idx) => idx + 1);
};

const computeSeatCode = (row, col, numbering, columns) => {
	if (!row || !col) return "";
	if (numbering === "numeric") {
		const colIndex = columns.indexOf(String(col));
		if (colIndex < 0) return "";
		return String((row - 1) * columns.length + colIndex + 1);
	}
	return `${row}${col}`;
};

const shouldAutoComputeSeatCode = (numbering) => numbering === "alpha";

const getNextNumericSeatCode = (objects) => {
	let max = 0;
	const used = new Set();
	(objects || []).forEach((obj) => {
		if (obj.object_type !== "seat") return;
		const raw = String(obj.seat_code || "").trim();
		if (!raw || !/^\d+$/.test(raw)) return;
		const val = parseInt(raw, 10);
		if (!Number.isNaN(val)) {
			used.add(val);
			if (val > max) max = val;
		}
	});
	let next = max + 1;
	while (used.has(next)) {
		next += 1;
	}
	return String(next);
};

const parseBetweenHint = (hint) => {
	const match = String(hint || "").match(
		/between_rows_(\d+)_(\d+)(?:_(left|right))?/i
	);
	if (!match) return null;
	return {
		afterRow: parseInt(match[1], 10),
		side: match[3] ? match[3].toLowerCase() : null,
	};
};

const parseOutsideRow = (hint) => {
	const match = String(hint || "").match(/row_(\d+)/i);
	if (!match) return null;
	const rowVal = parseInt(match[1], 10);
	return Number.isInteger(rowVal) ? rowVal : null;
};

const isBetweenObject = (object) => {
	if (!object) return false;
	return Boolean(parseBetweenHint(object.position_hint));
};

const getObjectLabel = (object) => {
	if (!object) return "";
	const rawType = String(object.object_type || "");
	const base = OBJECT_LABELS[rawType] || rawType.toUpperCase();
	return object.object_id ? `${base}:${object.object_id}` : base;
};

const DirectionBadge = ({ direction }) => {
	const normalized = String(direction || "").toLowerCase();
	const arrows = {
		forward: "↑",
		backward: "↓",
		left: "←",
		right: "→",
	};
	const arrow = arrows[normalized];
	if (!arrow) return null;
	return (
		<span className="absolute -top-1 -right-1 text-[10px] font-bold opacity-60">
			{arrow}
		</span>
	);
};

const SeatBackrest = ({ direction }) => {
	const normalized = String(direction || "").toLowerCase();
	const config = {
		forward: { position: "-left-2 top-2", size: "w-2 h-7", color: "bg-emerald-600/40" },
		backward: { position: "-right-2 top-2", size: "w-2 h-7", color: "bg-rose-500/40" },
		left: { position: "left-2 -top-2", size: "h-2 w-7", color: "bg-amber-500/40" },
		right: { position: "left-2 -bottom-2", size: "h-2 w-7", color: "bg-sky-500/40" },
	};
	const c = config[normalized] || { position: "-left-2 top-2", size: "w-2 h-7", color: "bg-slate-400/30" };
	return <div className={`absolute ${c.position} ${c.size} rounded-sm ${c.color}`} />;
};

const SeatCell = ({
	seatCode,
	direction,
	isSelected,
	isDropTarget,
	onClick,
	...dragProps
}) => {
	const normalized = String(direction || "").toLowerCase();
	const palette = {
		forward: "border-emerald-500 text-emerald-600",
		backward: "border-rose-400 text-rose-500",
		left: "border-amber-500 text-amber-600",
		right: "border-sky-500 text-sky-600",
		unknown: "border-slate-400 text-slate-500",
	};
	const border = palette[normalized] || palette.unknown;
	const ring = isSelected ? "ring-2 ring-blue-500" : "";
	const dropRing = isDropTarget ? "ring-2 ring-emerald-400" : "";
	return (
		<button
			type="button"
			onClick={onClick}
			className={`relative w-16 h-12 bg-white border-2 ${border} rounded-md shadow-sm flex flex-col items-center justify-center ${ring} ${dropRing}`}
			{...dragProps}
		>
			<SeatBackrest direction={direction} />
			<DirectionBadge direction={direction} />
			<span className="text-xs font-semibold">{seatCode}</span>
		</button>
	);
};

const EmptyCell = ({ isSelected, isDropTarget, onClick, ...dragProps }) => (
	<button
		type="button"
		onClick={onClick}
		{...dragProps}
		className={`w-16 h-12 bg-gray-50 border border-gray-200 rounded-md shadow-sm flex items-center justify-center ${
			isSelected ? "ring-2 ring-blue-500" : ""
		} ${isDropTarget ? "ring-2 ring-emerald-400" : ""}`}
	>
		<span className="text-gray-300 text-xs">+</span>
	</button>
);

const BlockedCell = ({ isSelected, isDropTarget, onClick, ...dragProps }) => (
	<button
		type="button"
		onClick={onClick}
		{...dragProps}
		className={`relative w-16 h-12 bg-gray-200 border border-gray-300 rounded-md shadow-sm overflow-hidden ${
			isSelected ? "ring-2 ring-blue-500" : ""
		} ${isDropTarget ? "ring-2 ring-emerald-400" : ""}`}
	>
		<div className="absolute inset-0 bg-[linear-gradient(135deg,transparent_45%,rgba(107,114,128,0.5)_45%,rgba(107,114,128,0.5)_55%,transparent_55%)]"></div>
	</button>
);

const ObjectCell = ({
	object,
	isSelected,
	isDropTarget,
	onClick,
	...dragProps
}) => {
	if (!object) return null;
	const dropRing = isDropTarget ? "ring-2 ring-emerald-400" : "";
	if (object.object_type === "table") {
		return (
			<button
				type="button"
				onClick={onClick}
				className={`${OBJECT_STYLES.table} relative ${
					isSelected ? "ring-2 ring-blue-500" : ""
				} ${dropRing}`}
				title={object?.notes || object?.position_hint || ""}
				{...dragProps}
			>
				TABLE
			</button>
		);
	}
	if (object.object_type === "wheelchair_space") {
		const dir = String(object?.direction || "").toLowerCase();
		const styleClass =
			dir === "backward"
				? OBJECT_STYLES.wheelchairBackward
				: OBJECT_STYLES.wheelchairForward;
		return (
			<button
				type="button"
				onClick={onClick}
				className={`${styleClass} relative ${
					isSelected ? "ring-2 ring-blue-500" : ""
				} ${dropRing}`}
				title={object?.notes || object?.position_hint || ""}
				{...dragProps}
			>
				<span className="text-base" role="img" aria-label="wheelchair">
					♿
				</span>
				<DirectionBadge direction={object?.direction} />
			</button>
		);
	}
	if (["stage", "wine_bar", "broadcast_room"].includes(object.object_type)) {
		const styleClass = OBJECT_STYLES[object.object_type] || OBJECT_STYLES.container;
		return (
			<button
				type="button"
				onClick={onClick}
				className={`${styleClass} relative ${
					isSelected ? "ring-2 ring-blue-500" : ""
				} ${dropRing}`}
				title={object?.notes || object?.position_hint || ""}
				{...dragProps}
			>
				{getObjectLabel(object)}
			</button>
		);
	}
	return (
		<button
			type="button"
			onClick={onClick}
			className={`${OBJECT_STYLES.container} relative ${
				isSelected ? "ring-2 ring-blue-500" : ""
			} ${dropRing}`}
			title={object?.notes || object?.position_hint || ""}
			{...dragProps}
		>
			{getObjectLabel(object)}
		</button>
	);
};

export default function SeatmapEditor() {
	const {
		trainTypes,
		layouts,
		isLoading,
		error,
		fetchTrainTypes,
		fetchLayouts,
		fetchLayout,
		createLayout,
		updateLayout,
		deleteLayout,
		clearError,
	} = useSeatmapStore();

	const [selectedTrainType, setSelectedTrainType] = useState("");
	const [selectedLayoutId, setSelectedLayoutId] = useState("");
	const [selectedCarNumber, setSelectedCarNumber] = useState("");
	const [draftLayout, setDraftLayout] = useState(null);
	const [columnsInput, setColumnsInput] = useState("");
	const [activeTool, setActiveTool] = useState("select");
	const [seatDirection, setSeatDirection] = useState("forward");
	const [tableSide, setTableSide] = useState("left");
	const [selectedObjectId, setSelectedObjectId] = useState(null);
	const [isLayoutModalOpen, setIsLayoutModalOpen] = useState(false);
	const [layoutForm, setLayoutForm] = useState({
		train_type_code: "",
		name: "",
		variant: "",
		source_url: "",
		source_snapshot_path: "",
	});
	const [isCarModalOpen, setIsCarModalOpen] = useState(false);
	const [carForm, setCarForm] = useState({
		car_number: "",
		label: "",
		image_url: "",
		image_path: "",
	});
	const [isSaving, setIsSaving] = useState(false);
	const [isLayoutDeleting, setIsLayoutDeleting] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [saveMessage, setSaveMessage] = useState("");
	const [saveError, setSaveError] = useState("");
	const [layoutLoading, setLayoutLoading] = useState(false);
	const [dragItem, setDragItem] = useState(null);
	const [dropTarget, setDropTarget] = useState(null);
	const layoutRequestRef = useRef(null);
	const loadedLayoutIdRef = useRef(null);

	useEffect(() => {
		fetchTrainTypes(true);
		fetchLayouts();
	}, [fetchTrainTypes, fetchLayouts]);

	const filteredLayouts = useMemo(() => {
		if (!selectedTrainType) return layouts;
		return layouts.filter((layout) => layout.train_type_code === selectedTrainType);
	}, [layouts, selectedTrainType]);

	useEffect(() => {
		if (!filteredLayouts.length) {
			setSelectedLayoutId("");
			return;
		}
		const normalizedId = selectedLayoutId ? String(selectedLayoutId) : "";
		const hasCurrent = filteredLayouts.some(
			(item) => String(item.id) === normalizedId
		);
		if (!hasCurrent) {
			setSelectedLayoutId(String(filteredLayouts[0].id));
		}
	}, [filteredLayouts, selectedLayoutId]);

	useEffect(() => {
		const normalizedId = selectedLayoutId ? String(selectedLayoutId) : "";
		if (!normalizedId) {
			setDraftLayout(null);
			loadedLayoutIdRef.current = null;
			return;
		}
		if (
			draftLayout?.id &&
			String(draftLayout.id) === normalizedId &&
			loadedLayoutIdRef.current === normalizedId
		) {
			return;
		}
		if (layoutRequestRef.current === normalizedId) {
			return;
		}
		const load = async () => {
			layoutRequestRef.current = normalizedId;
			setLayoutLoading(true);
			try {
				const layout = await fetchLayout(normalizedId);
				if (layoutRequestRef.current !== normalizedId) {
					return;
				}
				if (layout) {
					const hydrated = {
						...layout,
						cars: (layout.cars || []).map((car) => ({
							...car,
							layout: car.layout || {},
							objects: (car.objects || []).map((obj) => ({
								...obj,
								__editorId: obj.__editorId || makeEditorId(),
							})),
						})),
					};
					setDraftLayout(hydrated);
					const currentCar =
						hydrated.cars?.find(
							(car) => String(car.car_number) === String(selectedCarNumber)
						)?.car_number || "";
					const nextCar =
						currentCar || hydrated.cars?.[0]?.car_number || "";
					setSelectedCarNumber(nextCar);
					if (
						hydrated.train_type?.code &&
						hydrated.train_type.code !== selectedTrainType
					) {
						setSelectedTrainType(hydrated.train_type.code);
					}
					loadedLayoutIdRef.current = normalizedId;
				}
			} catch (err) {
				setSaveError(err.message || "좌석배치도 로딩 실패");
			} finally {
				if (layoutRequestRef.current === normalizedId) {
					layoutRequestRef.current = null;
					setLayoutLoading(false);
				}
			}
		};
		load();
	}, [selectedLayoutId, fetchLayout, draftLayout, selectedCarNumber, selectedTrainType]);

	useEffect(() => {
		if (!draftLayout || !selectedCarNumber) {
			setColumnsInput("");
			return;
		}
		const car = draftLayout.cars?.find(
			(item) => String(item.car_number) === String(selectedCarNumber)
		);
		const columns = resolveLayoutColumns(car?.layout, DEFAULT_LAYOUT.columns);
		setColumnsInput(columns ? columns.join(", ") : "");
	}, [draftLayout, selectedCarNumber]);

	useEffect(() => {
		if (!selectedObjectId) return;
		const exists = draftLayout?.cars
			?.flatMap((car) => car.objects || [])
			.some((obj) => obj.__editorId === selectedObjectId);
		if (!exists) {
			setSelectedObjectId(null);
		}
	}, [draftLayout, selectedObjectId]);

	const selectedCar = useMemo(() => {
		if (!draftLayout) return null;
		return draftLayout.cars?.find(
			(item) => String(item.car_number) === String(selectedCarNumber)
		);
	}, [draftLayout, selectedCarNumber]);

	const layoutConfig = selectedCar?.layout || {};
	const seatRows = useMemo(() => {
		const rows = new Set();
		(selectedCar?.objects || []).forEach((obj) => {
			const rowVal = parseInt(obj.row, 10);
			if (Number.isInteger(rowVal)) {
				rows.add(rowVal);
			}
		});
		return rows;
	}, [selectedCar]);

	const columns = useMemo(() => {
		const parsed = columnsInput ? parseColumnsInput(columnsInput) : null;
		return (
			resolveLayoutColumns(layoutConfig, parsed || DEFAULT_LAYOUT.columns) ||
			DEFAULT_LAYOUT.columns
		);
	}, [layoutConfig, columnsInput]);

	const numbering = resolveNumbering(layoutConfig);
	const aisleAfter = resolveAisleAfter(layoutConfig, columns);
	const rows = resolveRows(layoutConfig, seatRows);

	const {
		objectByRowCol,
		tableSlotsByAfterRow,
		partitionByAfterRow,
		outsideObjects,
		betweenRowObjects,
	} = useMemo(() => {
		const map = new Map();
		const tableSlots = new Map();
		const partitions = new Map();
		const outside = [];
		const betweenObjects = [];
		(selectedCar?.objects || []).forEach((obj) => {
			const rowVal = parseInt(obj.row, 10);
			const colVal =
				obj.col === null || obj.col === undefined ? null : String(obj.col);
			if (Number.isInteger(rowVal) && colVal) {
				map.set(`${rowVal}-${colVal}`, obj);
				return;
			}
			const hint = String(obj.position_hint || "");
			const between = hint.match(/between_rows_(\d+)_(\d+)(?:_(left|right))?/i);
			if (between) {
				const afterRow = parseInt(between[1], 10);
				const side = between[3] ? between[3].toLowerCase() : null;
				if (obj.object_type === "table" && side) {
					const slot = tableSlots.get(afterRow) || { left: null, right: null };
					slot[side] = obj;
					tableSlots.set(afterRow, slot);
					betweenObjects.push(obj);
					return;
				}
				if (obj.object_type === "partition") {
					partitions.set(afterRow, obj);
					betweenObjects.push(obj);
					return;
				}
			}
			outside.push(obj);
		});
		return {
			objectByRowCol: map,
			tableSlotsByAfterRow: tableSlots,
			partitionByAfterRow: partitions,
			outsideObjects: outside,
			betweenRowObjects: betweenObjects,
		};
	}, [selectedCar]);

	const outsideObjectsByRow = useMemo(() => {
		const map = new Map();
		const fallbackRow = rows[0] || 1;
		outsideObjects.forEach((obj) => {
			const rowVal = parseOutsideRow(obj.position_hint) || fallbackRow;
			const list = map.get(rowVal) || [];
			list.push(obj);
			map.set(rowVal, list);
		});
		return map;
	}, [outsideObjects, rows]);

	const selectedObject = useMemo(() => {
		if (!selectedObjectId || !selectedCar) return null;
		return (selectedCar.objects || []).find(
			(obj) => obj.__editorId === selectedObjectId
		);
	}, [selectedCar, selectedObjectId]);

	useEffect(() => {
		if (!selectedObject) return;
		if (
			["seat", "wheelchair_space"].includes(selectedObject.object_type) &&
			selectedObject.direction &&
			selectedObject.direction !== seatDirection
		) {
			setSeatDirection(selectedObject.direction);
		}
		if (selectedObject.object_type === "table") {
			const between = parseBetweenHint(selectedObject.position_hint);
			if (between?.side && between.side !== tableSide) {
				setTableSide(between.side);
			}
		}
	}, [selectedObject, seatDirection, tableSide]);

	const updateDraftLayout = (updater) => {
		setDraftLayout((prev) => {
			if (!prev) return prev;
			return updater(prev);
		});
	};

	const updateSelectedCar = (updater) => {
		updateDraftLayout((prev) => {
			const cars = (prev.cars || []).map((car) => {
				if (String(car.car_number) !== String(selectedCarNumber)) return car;
				return updater(car);
			});
			return { ...prev, cars };
		});
	};

	const updateLayoutConfig = (updates) => {
		updateSelectedCar((car) => ({
			...car,
			layout: {
				...car.layout,
				...updates,
			},
		}));
	};

	const buildObjectId = (base, objects) => {
		let next = base;
		let idx = 1;
		const existing = new Set(
			(objects || []).map((obj) => String(obj.object_id || ""))
		);
		while (next && existing.has(next)) {
			next = `${base}_${idx}`;
			idx += 1;
		}
		return next || null;
	};

	const resolveOutsideTool = (toolId) => {
		const tool = TOOL_OPTIONS.find((item) => item.id === toolId);
		if (!tool || tool.type === "action" || tool.type === "between") {
			return "toilet";
		}
		if (OUTSIDE_TOOL_IDS.has(tool.id)) {
			return tool.id;
		}
		return "toilet";
	};

	const upsertObject = (row, col, objectType) => {
		if (!selectedCar) return;
		const rowVal = parseInt(row, 10);
		const colVal = String(col);
		updateSelectedCar((car) => {
			const objects = [...(car.objects || [])];
			const targetIndex = objects.findIndex(
				(obj) => parseInt(obj.row, 10) === rowVal && String(obj.col) === colVal
			);
			const current = targetIndex >= 0 ? objects[targetIndex] : null;
			const isSeat = objectType === "seat";
			const autoSeatCode =
				isSeat && shouldAutoComputeSeatCode(numbering)
					? computeSeatCode(rowVal, colVal, numbering, columns)
					: null;
			const seatCode =
				isSeat && (autoSeatCode || current?.object_type === "seat")
					? autoSeatCode || current?.seat_code || null
					: null;
			const resolvedSeatCode =
				isSeat && !autoSeatCode && !seatCode
					? getNextNumericSeatCode(objects)
					: seatCode;
			const baseId = isSeat
				? resolvedSeatCode || `seat_${rowVal}${colVal}`
				: `${objectType}_${rowVal}${colVal}`;
			const objectId =
				current && current.object_type === objectType && current.object_id
					? current.object_id
					: buildObjectId(baseId, objects);
			const nextObject = {
				id: targetIndex >= 0 ? objects[targetIndex].id : null,
				object_type: objectType,
				object_id: objectId,
				seat_code: resolvedSeatCode || null,
				direction: isSeat || objectType === "wheelchair_space" ? seatDirection : null,
				row: rowVal,
				col: colVal,
				row_span: 1,
				col_span: 1,
				position_hint: "grid",
				notes: "",
				__editorId:
					targetIndex >= 0 ? objects[targetIndex].__editorId : makeEditorId(),
			};
			if (targetIndex >= 0) {
				objects[targetIndex] = nextObject;
			} else {
				objects.push(nextObject);
			}
			setSelectedObjectId(nextObject.__editorId);
			return { ...car, objects };
		});
	};

	const removeObjectAt = (row, col) => {
		if (!selectedCar) return;
		const rowVal = parseInt(row, 10);
		const colVal = String(col);
		updateSelectedCar((car) => {
			const nextObjects = (car.objects || []).filter(
				(obj) => !(parseInt(obj.row, 10) === rowVal && String(obj.col) === colVal)
			);
			return { ...car, objects: nextObjects };
		});
		setSelectedObjectId(null);
	};

	const selectObjectAt = (row, col) => {
		if (!selectedCar) return;
		const rowVal = parseInt(row, 10);
		const colVal = String(col);
		const target = (selectedCar.objects || []).find(
			(obj) => parseInt(obj.row, 10) === rowVal && String(obj.col) === colVal
		);
		setSelectedObjectId(target ? target.__editorId : null);
	};

	const addBetweenRowsObject = (row, objectType, side = null) => {
		if (!selectedCar) return;
		const rowVal = parseInt(row, 10);
		if (!Number.isInteger(rowVal)) return;
		if (!rows.includes(rowVal + 1)) {
			setSaveError("마지막 행에는 행 사이 객체를 추가할 수 없습니다.");
			return;
		}
		const hint =
			objectType === "table"
				? `between_rows_${rowVal}_${rowVal + 1}_${side}`
				: `between_rows_${rowVal}_${rowVal + 1}`;
		updateSelectedCar((car) => {
			const objects = [...(car.objects || [])];
			const baseId = `${objectType}_between_${rowVal}_${side || "center"}`;
			const objectId = buildObjectId(baseId, objects);
			const newObject = {
				id: null,
				object_type: objectType,
				object_id: objectId,
				seat_code: null,
				direction: null,
				row: null,
				col: null,
				row_span: 1,
				col_span: 1,
				position_hint: hint,
				notes: "",
				__editorId: makeEditorId(),
			};
			objects.push(newObject);
			setSelectedObjectId(newObject.__editorId);
			return { ...car, objects };
		});
	};

	const addOutsideObjectAtRow = (objectType, rowVal) => {
		if (!selectedCar) return;
		const targetRow = Number.isInteger(rowVal) ? rowVal : rows[0] || 1;
		updateSelectedCar((car) => {
			const objects = [...(car.objects || [])];
			const baseId = `${objectType}_outside_${targetRow}`;
			const objectId = buildObjectId(baseId, objects);
			const newObject = {
				id: null,
				object_type: objectType,
				object_id: objectId,
				seat_code: null,
				direction: objectType === "wheelchair_space" ? seatDirection : null,
				row: null,
				col: null,
				row_span: 1,
				col_span: 1,
				position_hint: `outside_right@row_${targetRow}`,
				notes: "",
				__editorId: makeEditorId(),
			};
			objects.push(newObject);
			setSelectedObjectId(newObject.__editorId);
			return { ...car, objects };
		});
	};

	const addOutsideObject = (objectType) => {
		addOutsideObjectAtRow(objectType, rows[0] || 1);
	};

	const updateSelectedObject = (updates) => {
		if (!selectedCar || !selectedObjectId) return;
		updateSelectedCar((car) => {
			const objects = (car.objects || []).map((obj) => {
				if (obj.__editorId !== selectedObjectId) return obj;
				const next = { ...obj, ...updates };
				if (next.object_type === "seat") {
					if (shouldAutoComputeSeatCode(numbering)) {
						const computed = computeSeatCode(
							parseInt(next.row, 10),
							next.col,
							numbering,
							columns
						);
						if (computed) {
							next.seat_code = computed;
						}
					} else if (
						updates.seat_code &&
						obj.object_id &&
						obj.object_id === obj.seat_code
					) {
						next.object_id = updates.seat_code;
					}
				} else {
					next.seat_code = null;
					if (next.object_type !== "wheelchair_space") {
						next.direction = null;
					}
				}
				return next;
			});
			return { ...car, objects };
		});
	};

	const handleSeatDirectionChange = (dir) => {
		setSeatDirection(dir);
		if (
			selectedObject &&
			["seat", "wheelchair_space"].includes(selectedObject.object_type)
		) {
			updateSelectedObject({ direction: dir });
		}
	};

	const handleTableSideChange = (nextSide) => {
		setTableSide(nextSide);
		if (selectedObject?.object_type !== "table") return;
		const between = parseBetweenHint(selectedObject.position_hint);
		if (!between?.side) return;
		if (between.side === nextSide) return;
		updateSelectedObject({
			position_hint: `between_rows_${between.afterRow}_${between.afterRow + 1}_${nextSide}`,
		});
	};

	const findObjectByEditorId = (editorId) => {
		if (!selectedCar) return null;
		return (selectedCar.objects || []).find(
			(obj) => obj.__editorId === editorId
		);
	};

	const activeDragObject = useMemo(() => {
		if (!dragItem || dragItem.kind !== "object") return null;
		return findObjectByEditorId(dragItem.objectId);
	}, [dragItem, selectedCar]);

	const showBetweenDrop = Boolean(
		dragItem &&
			((dragItem.kind === "tool" && BETWEEN_TOOL_IDS.has(dragItem.toolId)) ||
				(dragItem.kind === "object" &&
					activeDragObject &&
					["table", "partition"].includes(activeDragObject.object_type)))
	);
	const showOutsideDrop = Boolean(
		dragItem &&
			((dragItem.kind === "tool" && OUTSIDE_TOOL_IDS.has(dragItem.toolId)) ||
				dragItem.kind === "object")
	);

	const getObjectOrigin = (object) => {
		if (!object) return "unknown";
		const rowVal = parseInt(object.row, 10);
		const colVal =
			object.col === null || object.col === undefined
				? null
				: String(object.col);
		if (Number.isInteger(rowVal) && colVal) return "grid";
		if (isBetweenObject(object)) return "between";
		return "outside";
	};

	const applyGridPosition = (object, rowVal, colVal) => {
		const isSeat = object.object_type === "seat";
		const seatCode =
			isSeat && shouldAutoComputeSeatCode(numbering)
				? computeSeatCode(rowVal, colVal, numbering, columns)
				: object.seat_code || null;
		const next = {
			...object,
			row: rowVal,
			col: colVal,
			position_hint: "grid",
			seat_code: seatCode || null,
		};
		if (isSeat) {
			if (shouldAutoComputeSeatCode(numbering)) {
				next.object_id = seatCode || object.object_id;
			} else {
				next.object_id = object.object_id || null;
			}
			next.direction = object.direction || seatDirection;
		} else if (object.object_type === "wheelchair_space") {
			next.direction = object.direction || seatDirection;
		} else {
			next.direction = null;
		}
		return next;
	};

	const applyBetweenPosition = (object, afterRow, side) => ({
		...object,
		row: null,
		col: null,
		seat_code: null,
		position_hint:
			object.object_type === "table"
				? `between_rows_${afterRow}_${afterRow + 1}_${side}`
				: `between_rows_${afterRow}_${afterRow + 1}`,
		direction: null,
	});

	const applyOutsidePosition = (object, rowVal) => ({
		...object,
		row: null,
		col: null,
		seat_code: null,
		position_hint: `outside_right@row_${rowVal}`,
	});

	const swapGridObjects = (sourceObj, targetObj) => {
		if (!sourceObj || !targetObj) return;
		const sourceRow = parseInt(sourceObj.row, 10);
		const sourceCol =
			sourceObj.col === null || sourceObj.col === undefined
				? null
				: String(sourceObj.col);
		const targetRow = parseInt(targetObj.row, 10);
		const targetCol =
			targetObj.col === null || targetObj.col === undefined
				? null
				: String(targetObj.col);
		if (
			!Number.isInteger(sourceRow) ||
			!Number.isInteger(targetRow) ||
			!sourceCol ||
			!targetCol
		) {
			return;
		}
		updateSelectedCar((car) => {
			const objects = (car.objects || []).map((obj) => {
				if (obj.__editorId === sourceObj.__editorId) {
					return applyGridPosition(obj, targetRow, targetCol);
				}
				if (obj.__editorId === targetObj.__editorId) {
					return applyGridPosition(obj, sourceRow, sourceCol);
				}
				return obj;
			});
			return { ...car, objects };
		});
	};

	const moveObjectToCell = (editorId, row, col) => {
		const targetObj = findObjectByEditorId(editorId);
		if (!targetObj) return;
		const rowVal = parseInt(row, 10);
		const colVal = String(col);
		const existing = objectByRowCol.get(`${rowVal}-${colVal}`);
		if (existing && existing.__editorId !== editorId) {
			if (getObjectOrigin(targetObj) === "grid") {
				swapGridObjects(targetObj, existing);
				return;
			}
			setSaveError("이미 객체가 있는 셀입니다.");
			return;
		}
		updateSelectedCar((car) => {
			const objects = (car.objects || []).map((obj) => {
				if (obj.__editorId !== editorId) return obj;
				return applyGridPosition(obj, rowVal, colVal);
			});
			return { ...car, objects };
		});
		setSelectedObjectId(editorId);
	};

	const moveObjectToBetween = (editorId, afterRow, side) => {
		const targetObj = findObjectByEditorId(editorId);
		if (!targetObj) return;
		if (!["table", "partition"].includes(targetObj.object_type)) {
			setSaveError("행 사이 배치는 테이블/칸막이만 가능합니다.");
			return;
		}
		updateSelectedCar((car) => {
			const objects = (car.objects || []).map((obj) => {
				if (obj.__editorId !== editorId) return obj;
				return applyBetweenPosition(obj, afterRow, side);
			});
			return { ...car, objects };
		});
		setSelectedObjectId(editorId);
	};

	const moveObjectToOutside = (editorId, rowVal) => {
		const targetObj = findObjectByEditorId(editorId);
		if (!targetObj) return;
		updateSelectedCar((car) => {
			const objects = (car.objects || []).map((obj) => {
				if (obj.__editorId !== editorId) return obj;
				return applyOutsidePosition(obj, rowVal);
			});
			return { ...car, objects };
		});
		setSelectedObjectId(editorId);
	};

	const startToolDrag = (toolId) => (event) => {
		if (!event.dataTransfer) return;
		event.dataTransfer.effectAllowed = "copy";
		event.dataTransfer.setData("text/plain", `tool:${toolId}`);
		setDragItem({ kind: "tool", toolId });
		setActiveTool(toolId);
	};

	const startObjectDrag = (object) => (event) => {
		if (!event.dataTransfer) return;
		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData("text/plain", `object:${object.__editorId}`);
		setDragItem({ kind: "object", objectId: object.__editorId });
		setSelectedObjectId(object.__editorId);
	};

	const endDrag = () => {
		setDragItem(null);
		setDropTarget(null);
	};

	const resolveDragPayload = (event) => {
		if (dragItem) return dragItem;
		const raw = event?.dataTransfer?.getData("text/plain") || "";
		if (raw.startsWith("tool:")) {
			return { kind: "tool", toolId: raw.slice(5) };
		}
		if (raw.startsWith("object:")) {
			return { kind: "object", objectId: raw.slice(7) };
		}
		return null;
	};

	const canDropToCell = (payload) => {
		if (!payload) return false;
		if (payload.kind === "tool") {
			return (
				!BETWEEN_TOOL_IDS.has(payload.toolId) &&
				!["select", "erase"].includes(payload.toolId)
			);
		}
		return payload.kind === "object";
	};

	const canDropToBetween = (payload) => {
		if (!payload) return false;
		if (payload.kind === "tool") {
			return BETWEEN_TOOL_IDS.has(payload.toolId);
		}
		if (payload.kind === "object") {
			const obj = findObjectByEditorId(payload.objectId);
			return obj ? ["table", "partition"].includes(obj.object_type) : false;
		}
		return false;
	};

	const canDropToOutside = (payload) => {
		if (!payload) return false;
		if (payload.kind === "tool") {
			return OUTSIDE_TOOL_IDS.has(payload.toolId);
		}
		return payload.kind === "object";
	};

	const handleCellDragOver = (row, col) => (event) => {
		const payload = resolveDragPayload(event);
		if (!canDropToCell(payload)) return;
		event.preventDefault();
		event.dataTransfer.dropEffect = payload.kind === "tool" ? "copy" : "move";
		setDropTarget({ type: "cell", row, col });
	};

	const handleCellDrop = (row, col) => (event) => {
		const payload = resolveDragPayload(event);
		if (!canDropToCell(payload)) return;
		event.preventDefault();
		if (payload.kind === "tool") {
			upsertObject(row, col, payload.toolId);
		} else if (payload.kind === "object") {
			moveObjectToCell(payload.objectId, row, col);
		}
		setDropTarget(null);
	};

	const handleBetweenDragOver = (afterRow, side = null) => (event) => {
		const payload = resolveDragPayload(event);
		if (!canDropToBetween(payload)) return;
		if (payload.kind === "tool" && payload.toolId === "table_between" && !side) {
			return;
		}
		if (payload.kind === "object") {
			const obj = findObjectByEditorId(payload.objectId);
			if (obj?.object_type === "table" && !side) {
				return;
			}
		}
		event.preventDefault();
		event.dataTransfer.dropEffect = payload.kind === "tool" ? "copy" : "move";
		setDropTarget({ type: "between", afterRow, side });
	};

	const handleBetweenDrop = (afterRow, side = null) => (event) => {
		const payload = resolveDragPayload(event);
		if (!canDropToBetween(payload)) return;
		if (payload.kind === "tool" && payload.toolId === "table_between" && !side) {
			return;
		}
		event.preventDefault();
		if (payload.kind === "tool") {
			if (payload.toolId === "table_between") {
				addBetweenRowsObject(afterRow, "table", side);
			} else if (payload.toolId === "partition_between") {
				addBetweenRowsObject(afterRow, "partition");
			}
		} else if (payload.kind === "object") {
			const obj = findObjectByEditorId(payload.objectId);
			if (obj?.object_type === "table" && !side) {
				setSaveError("행 사이 테이블은 좌/우 영역에 배치해주세요.");
				return;
			}
			moveObjectToBetween(payload.objectId, afterRow, side);
		}
		setDropTarget(null);
	};

	const handleOutsideDragOver = (row) => (event) => {
		const payload = resolveDragPayload(event);
		if (!canDropToOutside(payload)) return;
		event.preventDefault();
		event.dataTransfer.dropEffect = payload.kind === "tool" ? "copy" : "move";
		setDropTarget({ type: "outside", row });
	};

	const handleOutsideDrop = (row) => (event) => {
		const payload = resolveDragPayload(event);
		if (!canDropToOutside(payload)) return;
		event.preventDefault();
		if (payload.kind === "tool") {
			addOutsideObjectAtRow(payload.toolId, row);
		} else if (payload.kind === "object") {
			moveObjectToOutside(payload.objectId, row);
		}
		setDropTarget(null);
	};

	const rotateDirection = (direction) => {
		const normalized = String(direction || "forward");
		const idx = DIRECTION_ORDER.indexOf(normalized);
		if (idx === -1) return DIRECTION_ORDER[0];
		return DIRECTION_ORDER[(idx + 1) % DIRECTION_ORDER.length];
	};

	const handleCellClick = (row, col) => {
		if (activeTool === "select") {
			selectObjectAt(row, col);
			return;
		}
		if (activeTool === "erase") {
			removeObjectAt(row, col);
			return;
		}
		if (activeTool === "table_between" || activeTool === "partition_between") {
			return;
		}
		upsertObject(row, col, activeTool);
	};

	const handleRowHeaderClick = (row) => {
		if (activeTool === "table_between") {
			addBetweenRowsObject(row, "table", tableSide);
		}
		if (activeTool === "partition_between") {
			addBetweenRowsObject(row, "partition");
		}
	};

	const handleSave = async () => {
		if (!draftLayout || !selectedCar) return;
		setSaveError("");
		setSaveMessage("");
		setIsSaving(true);
		try {
			const objectIds = new Set();
			const seatCodes = new Set();
			const rowCols = new Set();
			(selectedCar.objects || []).forEach((obj) => {
				const rowVal = parseInt(obj.row, 10);
				const colVal =
					obj.col === null || obj.col === undefined ? null : String(obj.col);
				if (Number.isInteger(rowVal) && colVal) {
					const key = `${rowVal}-${colVal}`;
					if (rowCols.has(key)) {
						throw new Error(`중복 위치: ${key}`);
					}
					rowCols.add(key);
				}
				if (obj.object_id) {
					if (objectIds.has(obj.object_id)) {
						throw new Error(`중복 object_id: ${obj.object_id}`);
					}
					objectIds.add(obj.object_id);
				}
				if (obj.object_type === "seat") {
					const computed = shouldAutoComputeSeatCode(numbering)
						? computeSeatCode(rowVal, colVal, numbering, columns)
						: null;
					const code = obj.seat_code ? String(obj.seat_code) : computed;
					if (code) {
						if (seatCodes.has(code)) {
							throw new Error(`중복 seat_code: ${code}`);
						}
						seatCodes.add(code);
					}
				}
			});

			const sanitizedObjects = (selectedCar.objects || []).map((obj) => {
				const rowVal = parseInt(obj.row, 10);
				const colVal =
					obj.col === null || obj.col === undefined ? null : String(obj.col);
				const autoSeatCode =
					obj.object_type === "seat" && shouldAutoComputeSeatCode(numbering)
						? computeSeatCode(rowVal, colVal, numbering, columns)
						: null;
				const seatCode =
					obj.object_type === "seat"
						? autoSeatCode || obj.seat_code || null
						: obj.seat_code || null;
				return {
					id: obj.id || undefined,
					object_type: obj.object_type,
					object_id: obj.object_id || null,
					seat_code: seatCode || null,
					direction: obj.direction || null,
					row: Number.isInteger(rowVal) ? rowVal : null,
					col: colVal || null,
					row_span: obj.row_span || 1,
					col_span: obj.col_span || 1,
					position_hint: obj.position_hint || null,
					notes: obj.notes || null,
				};
			});

			const payload = {
				name: draftLayout.name,
				variant: draftLayout.variant || null,
				source_url: draftLayout.source_url || null,
				source_snapshot_path: draftLayout.source_snapshot_path || null,
				cars: [
					{
						id: selectedCar.id || undefined,
						car_number: selectedCar.car_number,
						label: selectedCar.label || null,
						image_url: selectedCar.image_url || null,
						image_path: selectedCar.image_path || null,
						layout: {
							...selectedCar.layout,
							columns,
							seat_columns: columns,
							aisle_after: aisleAfter,
							numbering,
						},
						objects: sanitizedObjects,
					},
				],
			};

			const updated = await updateLayout(draftLayout.id, payload);
			if (updated) {
				const hydrated = {
					...updated,
					cars: (updated.cars || []).map((car) => ({
						...car,
						layout: car.layout || {},
						objects: (car.objects || []).map((obj) => ({
							...obj,
							__editorId: obj.__editorId || makeEditorId(),
						})),
					})),
				};
				setDraftLayout(hydrated);
				setSaveMessage("좌석배치도 저장 완료");
			}
		} catch (err) {
			setSaveError(err.message || "좌석배치도 저장 실패");
		} finally {
			setIsSaving(false);
		}
	};

	const openLayoutModal = () => {
		setLayoutForm((prev) => ({
			...prev,
			train_type_code: selectedTrainType || prev.train_type_code,
		}));
		setIsLayoutModalOpen(true);
	};

	const handleCreateLayout = async (event) => {
		event.preventDefault();
		setSaveError("");
		try {
			const created = await createLayout({
				train_type_code: layoutForm.train_type_code,
				name: layoutForm.name,
				variant: layoutForm.variant || null,
				source_url: layoutForm.source_url || null,
				source_snapshot_path: layoutForm.source_snapshot_path || null,
			});
			setIsLayoutModalOpen(false);
			setLayoutForm({
				train_type_code: "",
				name: "",
				variant: "",
				source_url: "",
				source_snapshot_path: "",
			});
			if (created) {
				setSelectedLayoutId(String(created.id));
			}
		} catch (err) {
			setSaveError(err.message || "레이아웃 생성 실패");
		}
	};

	const handleAddCar = (event) => {
		event.preventDefault();
		if (!draftLayout) return;
		const carNumber = carForm.car_number.trim();
		if (!carNumber) {
			setSaveError("호차 번호를 입력해주세요.");
			return;
		}
		updateDraftLayout((prev) => {
			const exists = (prev.cars || []).some(
				(car) => String(car.car_number) === carNumber
			);
			if (exists) {
				setSaveError("이미 존재하는 호차입니다.");
				return prev;
			}
			const templateLayout = prev.cars?.[0]?.layout || DEFAULT_LAYOUT;
			const newCar = {
				id: null,
				car_number: carNumber,
				label: carForm.label || "",
				image_url: carForm.image_url || "",
				image_path: carForm.image_path || "",
				layout: { ...templateLayout },
				objects: [],
			};
			return { ...prev, cars: [...(prev.cars || []), newCar] };
		});
		setSelectedCarNumber(carNumber);
		setIsCarModalOpen(false);
		setCarForm({ car_number: "", label: "", image_url: "", image_path: "" });
	};

	const handleDeleteLayout = async () => {
		if (!draftLayout) return;
		setSaveError("");
		setSaveMessage("");
		if (
			!window.confirm(
				`레이아웃 \"${draftLayout.name}\"을(를) 삭제할까요? 이 작업은 되돌릴 수 없습니다.`
			)
		) {
			return;
		}

		setIsLayoutDeleting(true);
		try {
			await deleteLayout(draftLayout.id);
			setDraftLayout(null);
			setSelectedLayoutId("");
			setSelectedCarNumber("");
			setSelectedObjectId(null);
			setSaveMessage("레이아웃 삭제 완료");
		} catch (err) {
			setSaveError(err.message || "레이아웃 삭제 실패");
		} finally {
			setIsLayoutDeleting(false);
		}
	};

	const handleDeleteCar = async () => {
		if (!draftLayout || !selectedCar) return;
		setSaveError("");
		setSaveMessage("");
		if (
			!window.confirm(
				`${selectedCar.car_number}호차를 삭제할까요? 이 작업은 되돌릴 수 없습니다.`
			)
		) {
			return;
		}

		const remainingCars = (draftLayout.cars || []).filter(
			(car) => String(car.car_number) !== String(selectedCar.car_number)
		);
		if (remainingCars.length === 0) {
			setSaveError("마지막 호차는 삭제할 수 없습니다.");
			return;
		}

		if (!selectedCar.id) {
			updateDraftLayout((prev) => ({
				...prev,
				cars: remainingCars,
			}));
			setSelectedCarNumber(String(remainingCars[0]?.car_number || ""));
			setSelectedObjectId(null);
			setSaveMessage("호차 삭제 완료");
			return;
		}

		const payloadCars = remainingCars
			.filter((car) => car.id)
			.map((car) => ({
				id: car.id,
				car_number: car.car_number,
			}));
		if (payloadCars.length === 0) {
			setSaveError("마지막 호차는 삭제할 수 없습니다.");
			return;
		}

		setIsDeleting(true);
		try {
			const updated = await updateLayout(draftLayout.id, {
				cars: payloadCars,
				replace_cars: true,
			});
			if (updated) {
				const hydrated = {
					...updated,
					cars: (updated.cars || []).map((car) => ({
						...car,
						layout: car.layout || {},
						objects: (car.objects || []).map((obj) => ({
							...obj,
							__editorId: obj.__editorId || makeEditorId(),
						})),
					})),
				};
				setDraftLayout(hydrated);
				setSelectedCarNumber(String(hydrated.cars?.[0]?.car_number || ""));
				setSelectedObjectId(null);
				setSaveMessage("호차 삭제 완료");
			}
		} catch (err) {
			setSaveError(err.message || "호차 삭제 실패");
		} finally {
			setIsDeleting(false);
		}
	};

	const renderBetweenRow = (row) => {
		const tableSlot = tableSlotsByAfterRow.get(row);
		const partition = partitionByAfterRow.get(row);
		if (!tableSlot && !partition && !showBetweenDrop) return null;
		const leftCols = columns.slice(0, aisleAfter);
		const rightCols = columns.slice(aisleAfter);
		const leftActive =
			dropTarget?.type === "between" &&
			dropTarget.afterRow === row &&
			dropTarget.side === "left";
		const rightActive =
			dropTarget?.type === "between" &&
			dropTarget.afterRow === row &&
			dropTarget.side === "right";
		const centerActive =
			dropTarget?.type === "between" &&
			dropTarget.afterRow === row &&
			!dropTarget.side;

		return (
			<div key={`between-${row}`} className="flex items-center mb-2">
				<div className="w-8"></div>
				<div
					className={`flex gap-2 ${leftActive ? "bg-amber-100/70 rounded" : ""}`}
					onDragOver={handleBetweenDragOver(row, "left")}
					onDrop={handleBetweenDrop(row, "left")}
				>
					{leftCols.map((col) => (
						<div
							key={`left-${row}-${col}`}
							className="w-16 h-3 flex items-center justify-center"
						>
							{tableSlot?.left && (
								<button
									type="button"
									draggable
									onDragStart={startObjectDrag(tableSlot.left)}
									onDragEnd={endDrag}
									onClick={() => setSelectedObjectId(tableSlot.left.__editorId)}
									className={`w-full h-1 rounded cursor-grab ${
										tableSlot.left.__editorId === selectedObjectId
											? "bg-amber-500"
											: "bg-amber-300"
									}`}
								></button>
							)}
						</div>
					))}
				</div>
				<div
					className={`w-8 h-3 ${centerActive ? "bg-blue-200/70 rounded" : ""}`}
					onDragOver={handleBetweenDragOver(row)}
					onDrop={handleBetweenDrop(row)}
				></div>
				<div
					className={`flex gap-2 ${rightActive ? "bg-amber-100/70 rounded" : ""}`}
					onDragOver={handleBetweenDragOver(row, "right")}
					onDrop={handleBetweenDrop(row, "right")}
				>
					{rightCols.map((col) => (
						<div
							key={`right-${row}-${col}`}
							className="w-16 h-3 flex items-center justify-center"
						>
							{tableSlot?.right && (
								<button
									type="button"
									draggable
									onDragStart={startObjectDrag(tableSlot.right)}
									onDragEnd={endDrag}
									onClick={() => setSelectedObjectId(tableSlot.right.__editorId)}
									className={`w-full h-1 rounded cursor-grab ${
										tableSlot.right.__editorId === selectedObjectId
											? "bg-amber-500"
											: "bg-amber-300"
									}`}
								></button>
							)}
						</div>
					))}
				</div>
				{partition && (
					<button
						type="button"
						draggable
						onDragStart={startObjectDrag(partition)}
						onDragEnd={endDrag}
						onClick={() => setSelectedObjectId(partition.__editorId)}
						className={`ml-2 text-xs cursor-grab ${
							partition.__editorId === selectedObjectId
								? "text-blue-600 font-semibold"
								: "text-gray-500"
						}`}
					>
						칸막이
					</button>
				)}
			</div>
		);
	};

	return (
		<div className="py-6">
			<div className="flex flex-wrap items-center justify-between gap-4 mb-6">
				<h1 className="text-2xl font-bold text-gray-900">
					좌석배치도 편집기
				</h1>
				<div className="flex items-center gap-3">
					<button
						onClick={openLayoutModal}
						disabled={isLayoutDeleting}
						className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
					>
						<PlusIcon className="w-4 h-4" />
						새 레이아웃
					</button>
					<button
						onClick={handleDeleteLayout}
						disabled={!draftLayout || isLayoutDeleting}
						className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-60"
					>
						{isLayoutDeleting ? (
							<ArrowPathIcon className="w-4 h-4 animate-spin" />
						) : (
							<TrashIcon className="w-4 h-4" />
						)}
						레이아웃 삭제
					</button>
					<button
						onClick={handleSave}
						disabled={
							isSaving || isDeleting || isLayoutDeleting || !selectedCar
						}
						className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-60"
					>
						{isSaving ? (
							<ArrowPathIcon className="w-4 h-4 animate-spin" />
						) : (
							<CheckCircleIcon className="w-4 h-4" />
						)}
						선택 호차 저장
					</button>
				</div>
			</div>

			{(error || saveError) && (
				<div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
					{saveError || error}
					<button
						onClick={() => {
							setSaveError("");
							clearError();
						}}
						className="ml-2 underline"
					>
						닫기
					</button>
				</div>
			)}

			{saveMessage && (
				<div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700">
					{saveMessage}
				</div>
			)}

			<div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
				<div className="space-y-4">
					<div className="bg-white shadow rounded-lg p-4">
						<div className="flex flex-wrap items-end gap-4">
							<div className="min-w-[160px]">
								<label className="block text-xs font-semibold text-gray-500 mb-1">
									열차종
								</label>
								<select
									value={selectedTrainType}
									onChange={(event) =>
										setSelectedTrainType(event.target.value)
									}
									className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm"
								>
									<option value="">전체</option>
									{trainTypes.map((type) => (
										<option key={type.code} value={type.code}>
											{type.name} ({type.code}) · {type.category === "bus" ? "버스" : "열차"}
										</option>
									))}
								</select>
							</div>
							<div className="min-w-[200px]">
								<label className="block text-xs font-semibold text-gray-500 mb-1">
									레이아웃
								</label>
								<select
									value={selectedLayoutId}
									onChange={(event) => {
										const nextId = String(event.target.value);
										setSelectedLayoutId(nextId);
										const target = layouts.find(
											(item) => String(item.id) === nextId
										);
										if (target) {
											setSelectedTrainType(target.train_type_code);
										}
									}}
									className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm"
								>
									{filteredLayouts.map((layout) => (
										<option key={layout.id} value={String(layout.id)}>
											{layout.train_type_name} · {layout.name}
											{layout.variant ? ` (${layout.variant})` : ""}
										</option>
									))}
								</select>
							</div>
							<div className="min-w-[160px]">
								<label className="block text-xs font-semibold text-gray-500 mb-1">
									호차
								</label>
								<select
									value={selectedCarNumber}
									onChange={(event) => setSelectedCarNumber(event.target.value)}
									className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm"
									disabled={!draftLayout}
								>
									{(draftLayout?.cars || []).map((car) => (
										<option key={car.car_number} value={car.car_number}>
											{car.car_number}호차
										</option>
									))}
								</select>
							</div>
							<button
								onClick={() => setIsCarModalOpen(true)}
								disabled={!draftLayout || isDeleting || isLayoutDeleting}
								className="px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200 disabled:opacity-60"
							>
								호차 추가
							</button>
							<button
								onClick={handleDeleteCar}
								disabled={!draftLayout || !selectedCar || isDeleting}
								className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg text-sm hover:bg-red-100 disabled:opacity-60"
							>
								{isDeleting ? (
									<ArrowPathIcon className="w-4 h-4 animate-spin" />
								) : (
									<TrashIcon className="w-4 h-4" />
								)}
								호차 삭제
							</button>
						</div>
					</div>

					<div className="bg-white shadow rounded-lg p-4">
						{layoutLoading ? (
							<div className="flex items-center justify-center py-12 text-gray-500">
								<ArrowPathIcon className="w-5 h-5 animate-spin" />
								<span className="ml-2">레이아웃 로딩중...</span>
							</div>
						) : !selectedCar ? (
							<div className="text-gray-500 text-center py-12">
								편집할 레이아웃을 선택해주세요.
							</div>
						) : (
							<div className="overflow-x-auto">
								<div className="min-w-max mx-auto">
									{selectedCar.image_url && (
										<div className="mb-4 flex justify-center">
											<img
												src={selectedCar.image_url}
												alt={`${selectedCar.car_number}호차 이미지`}
												className="max-h-64 object-contain border rounded"
											/>
										</div>
									)}
									<div className="flex items-center mb-2">
										<div className="w-8"></div>
										<div className="flex gap-2">
											{columns.slice(0, aisleAfter).map((col) => (
												<div key={`header-left-${col}`} className="w-16 text-center font-bold">
													{col}
												</div>
											))}
										</div>
										<div className="w-8"></div>
										<div className="flex gap-2">
											{columns.slice(aisleAfter).map((col) => (
												<div key={`header-right-${col}`} className="w-16 text-center font-bold">
													{col}
												</div>
											))}
										</div>
										<div className="w-16 text-center text-xs font-semibold text-gray-400">
											외부
										</div>
									</div>

									{rows.map((row) => (
										<React.Fragment key={`row-${row}`}>
											<div className="flex items-center mb-2">
												<button
													type="button"
													onClick={() => handleRowHeaderClick(row)}
													className="w-8 text-center font-bold text-gray-700 hover:text-blue-600"
												>
													{row}
												</button>
												<div className="flex gap-2">
													{columns.slice(0, aisleAfter).map((col) => {
														const key = `${row}-${col}`;
														const object = objectByRowCol.get(key);
														const isSelected =
															object?.__editorId === selectedObjectId;
														const isDropTarget =
															dropTarget?.type === "cell" &&
															dropTarget.row === row &&
															dropTarget.col === col;
														const dragHandlers = {
															onDragOver: handleCellDragOver(row, col),
															onDrop: handleCellDrop(row, col),
														};
														if (object?.object_type === "seat") {
															const seatCode =
																object.seat_code ||
																(shouldAutoComputeSeatCode(numbering)
																	? computeSeatCode(row, col, numbering, columns)
																	: "") ||
																"";
															return (
																<SeatCell
																	key={key}
																	seatCode={seatCode || key}
																	direction={object.direction}
																	isSelected={isSelected}
																	isDropTarget={isDropTarget}
																	onClick={() => handleCellClick(row, col)}
																	draggable
																	onDragStart={startObjectDrag(object)}
																	onDragEnd={endDrag}
																	{...dragHandlers}
																/>
															);
														}
														if (object?.object_type === "blocked") {
															return (
																<BlockedCell
																	key={key}
																	isSelected={isSelected}
																	isDropTarget={isDropTarget}
																	onClick={() => handleCellClick(row, col)}
																	draggable
																	onDragStart={startObjectDrag(object)}
																	onDragEnd={endDrag}
																	{...dragHandlers}
																/>
															);
														}
														if (object) {
															return (
																<ObjectCell
																	key={key}
																	object={object}
																	isSelected={isSelected}
																	isDropTarget={isDropTarget}
																	onClick={() => handleCellClick(row, col)}
																	draggable
																	onDragStart={startObjectDrag(object)}
																	onDragEnd={endDrag}
																	{...dragHandlers}
																/>
															);
														}
														return (
															<EmptyCell
																key={key}
																isSelected={isSelected}
																isDropTarget={isDropTarget}
																onClick={() => handleCellClick(row, col)}
																{...dragHandlers}
															/>
														);
													})}
												</div>
												<div className="w-8"></div>
												<div className="flex gap-2">
													{columns.slice(aisleAfter).map((col) => {
														const key = `${row}-${col}`;
														const object = objectByRowCol.get(key);
														const isSelected =
															object?.__editorId === selectedObjectId;
														const isDropTarget =
															dropTarget?.type === "cell" &&
															dropTarget.row === row &&
															dropTarget.col === col;
														const dragHandlers = {
															onDragOver: handleCellDragOver(row, col),
															onDrop: handleCellDrop(row, col),
														};
														if (object?.object_type === "seat") {
															const seatCode =
																object.seat_code ||
																(shouldAutoComputeSeatCode(numbering)
																	? computeSeatCode(row, col, numbering, columns)
																	: "") ||
																"";
															return (
																<SeatCell
																	key={key}
																	seatCode={seatCode || key}
																	direction={object.direction}
																	isSelected={isSelected}
																	isDropTarget={isDropTarget}
																	onClick={() => handleCellClick(row, col)}
																	draggable
																	onDragStart={startObjectDrag(object)}
																	onDragEnd={endDrag}
																	{...dragHandlers}
																/>
															);
														}
														if (object?.object_type === "blocked") {
															return (
																<BlockedCell
																	key={key}
																	isSelected={isSelected}
																	isDropTarget={isDropTarget}
																	onClick={() => handleCellClick(row, col)}
																	draggable
																	onDragStart={startObjectDrag(object)}
																	onDragEnd={endDrag}
																	{...dragHandlers}
																/>
															);
														}
														if (object) {
															return (
																<ObjectCell
																	key={key}
																	object={object}
																	isSelected={isSelected}
																	isDropTarget={isDropTarget}
																	onClick={() => handleCellClick(row, col)}
																	draggable
																	onDragStart={startObjectDrag(object)}
																	onDragEnd={endDrag}
																	{...dragHandlers}
																/>
															);
														}
														return (
															<EmptyCell
																key={key}
																isSelected={isSelected}
																isDropTarget={isDropTarget}
																onClick={() => handleCellClick(row, col)}
																{...dragHandlers}
															/>
														);
													})}
												</div>
												<div
													className={`ml-2 w-16 min-h-[3rem] rounded border ${
														showOutsideDrop
															? "border-dashed border-emerald-200"
															: "border-transparent"
													} ${
														dropTarget?.type === "outside" &&
														dropTarget.row === row
															? "bg-emerald-50 ring-2 ring-emerald-400"
															: ""
													}`}
													onDragOver={
														showOutsideDrop ? handleOutsideDragOver(row) : undefined
													}
													onDrop={showOutsideDrop ? handleOutsideDrop(row) : undefined}
												>
													<div className="flex flex-col gap-1 p-1">
														{(outsideObjectsByRow.get(row) || []).map((obj) => (
															<button
																key={obj.__editorId}
																type="button"
																draggable
																onDragStart={startObjectDrag(obj)}
																onDragEnd={endDrag}
																onClick={() => setSelectedObjectId(obj.__editorId)}
																className={`text-[10px] px-1 py-0.5 rounded border ${
																	obj.__editorId === selectedObjectId
																		? "border-blue-500 bg-blue-50 text-blue-700"
																		: "border-gray-200 bg-gray-50 text-gray-600"
																}`}
																title={obj.position_hint || ""}
															>
																{getObjectLabel(obj)}
															</button>
														))}
													</div>
												</div>
											</div>
											{renderBetweenRow(row)}
										</React.Fragment>
									))}
								</div>
							</div>
						)}
					</div>

					{(outsideObjects.length > 0 || betweenRowObjects.length > 0) && (
						<div className="bg-white shadow rounded-lg p-4">
							<h3 className="text-sm font-semibold text-gray-700 mb-2">
								외부/행 사이 객체
							</h3>
							<div className="flex flex-wrap gap-2 text-xs text-gray-600">
								{betweenRowObjects.map((obj) => (
									<button
										key={obj.__editorId}
										type="button"
										draggable
										onDragStart={startObjectDrag(obj)}
										onDragEnd={endDrag}
										onClick={() => setSelectedObjectId(obj.__editorId)}
										className={`px-2 py-1 rounded border ${
											obj.__editorId === selectedObjectId
												? "border-blue-500 bg-blue-50 text-blue-700"
												: "border-gray-200 bg-gray-50"
										}`}
										title={obj.position_hint || ""}
									>
										{getObjectLabel(obj)}
									</button>
								))}
								{outsideObjects.map((obj) => (
									<button
										key={obj.__editorId}
										type="button"
										draggable
										onDragStart={startObjectDrag(obj)}
										onDragEnd={endDrag}
										onClick={() => setSelectedObjectId(obj.__editorId)}
										className={`px-2 py-1 rounded border ${
											obj.__editorId === selectedObjectId
												? "border-blue-500 bg-blue-50 text-blue-700"
												: "border-gray-200 bg-gray-50"
										}`}
										title={obj.position_hint || ""}
									>
										{getObjectLabel(obj)}
									</button>
								))}
							</div>
						</div>
					)}
				</div>

				<div className="space-y-4">
					<div className="bg-white shadow rounded-lg p-4">
						<h3 className="text-sm font-semibold text-gray-700 mb-3">도구</h3>
						<div className="grid grid-cols-2 gap-2">
							{TOOL_OPTIONS.map((tool) => (
								<button
									key={tool.id}
									type="button"
									draggable={tool.type !== "action"}
									onDragStart={
										tool.type !== "action" ? startToolDrag(tool.id) : undefined
									}
									onDragEnd={tool.type !== "action" ? endDrag : undefined}
									onClick={() => setActiveTool(tool.id)}
									className={`px-2 py-2 rounded border text-xs ${
										activeTool === tool.id
											? "border-blue-500 bg-blue-50 text-blue-700"
											: "border-gray-200 text-gray-600 hover:bg-gray-50"
									} ${tool.type !== "action" ? "cursor-grab" : ""}`}
								>
									{tool.label}
								</button>
							))}
						</div>
						<p className="mt-2 text-[11px] text-gray-500">
							도구를 드래그해서 좌석 그리드/행 사이/외부 레인에 놓을 수 있어요.
							<span className="ml-1 text-gray-400">
								(칸막이: 셀/행 사이 분리)
							</span>
						</p>
						<div className="mt-3">
							<button
								type="button"
								onClick={() => addOutsideObject(resolveOutsideTool(activeTool))}
								className="w-full px-3 py-2 text-xs bg-gray-100 rounded-lg hover:bg-gray-200"
							>
								외부 객체 추가
							</button>
						</div>
					</div>

					<div className="bg-white shadow rounded-lg p-4">
						<h3 className="text-sm font-semibold text-gray-700 mb-3">
							좌석 방향
						</h3>
						<div className="grid grid-cols-2 gap-2">
							{DIRECTION_ORDER.map((dir) => (
								<button
									key={dir}
									type="button"
									onClick={() => handleSeatDirectionChange(dir)}
									className={`px-2 py-2 rounded border text-xs ${
										seatDirection === dir
											? "border-emerald-500 bg-emerald-50 text-emerald-700"
											: "border-gray-200 text-gray-600 hover:bg-gray-50"
									}`}
								>
									{DIRECTION_LABELS[dir]}
								</button>
							))}
						</div>
						<div className="mt-3 flex items-center gap-2">
							<label className="text-xs text-gray-500">행 사이 테이블 방향</label>
							<select
								value={tableSide}
								onChange={(event) => handleTableSideChange(event.target.value)}
								className="border border-gray-300 rounded px-2 py-1 text-xs"
							>
								<option value="left">왼쪽</option>
								<option value="right">오른쪽</option>
							</select>
						</div>
					</div>

					<div className="bg-white shadow rounded-lg p-4">
						<h3 className="text-sm font-semibold text-gray-700 mb-3">
							레이아웃 설정
						</h3>
						<div className="space-y-3 text-xs">
							<div>
								<label className="block text-gray-500 mb-1">레이아웃 이름</label>
								<input
									type="text"
									value={draftLayout?.name || ""}
									onChange={(event) =>
										updateDraftLayout((prev) => ({
											...prev,
											name: event.target.value,
										}))
									}
									className="w-full border border-gray-300 rounded px-2 py-1"
								/>
							</div>
							<div>
								<label className="block text-gray-500 mb-1">Variant</label>
								<input
									type="text"
									value={draftLayout?.variant || ""}
									onChange={(event) =>
										updateDraftLayout((prev) => ({
											...prev,
											variant: event.target.value,
										}))
									}
									className="w-full border border-gray-300 rounded px-2 py-1"
								/>
							</div>
							<div>
								<label className="block text-gray-500 mb-1">행 범위</label>
								<div className="flex gap-2">
									<input
										type="number"
										value={layoutConfig?.row_start || ""}
										onChange={(event) =>
											updateLayoutConfig({
												row_start: parseInt(event.target.value, 10) || "",
											})
										}
										className="w-full border border-gray-300 rounded px-2 py-1"
										placeholder="시작"
									/>
									<input
										type="number"
										value={layoutConfig?.row_end || ""}
										onChange={(event) =>
											updateLayoutConfig({
												row_end: parseInt(event.target.value, 10) || "",
											})
										}
										className="w-full border border-gray-300 rounded px-2 py-1"
										placeholder="끝"
									/>
								</div>
							</div>
							<div>
								<label className="block text-gray-500 mb-1">열</label>
								<input
									type="text"
									value={columnsInput}
									onChange={(event) => setColumnsInput(event.target.value)}
									onBlur={() => {
										const parsed = parseColumnsInput(columnsInput);
										if (parsed.length > 0) {
											updateLayoutConfig({ columns: parsed, seat_columns: parsed });
										}
									}}
									className="w-full border border-gray-300 rounded px-2 py-1"
									placeholder="A,B,C,D"
								/>
							</div>
							<div>
								<label className="block text-gray-500 mb-1">통로 위치</label>
								<input
									type="number"
									value={aisleAfter}
									onChange={(event) =>
										updateLayoutConfig({
											aisle_after: parseInt(event.target.value, 10) || 0,
										})
									}
									className="w-full border border-gray-300 rounded px-2 py-1"
								/>
							</div>
							<div>
								<label className="block text-gray-500 mb-1">번호 체계</label>
								<select
									value={numbering}
									onChange={(event) =>
										updateLayoutConfig({ numbering: event.target.value })
									}
									className="w-full border border-gray-300 rounded px-2 py-1"
								>
									<option value="alpha">알파벳</option>
									<option value="numeric">숫자</option>
								</select>
							</div>
							<div>
								<label className="block text-gray-500 mb-1">방향</label>
								<select
									value={layoutConfig?.orientation || "horizontal"}
									onChange={(event) =>
										updateLayoutConfig({ orientation: event.target.value })
									}
									className="w-full border border-gray-300 rounded px-2 py-1"
								>
									<option value="horizontal">가로</option>
									<option value="vertical">세로</option>
								</select>
							</div>
						</div>
					</div>

					<div className="bg-white shadow rounded-lg p-4">
						<h3 className="text-sm font-semibold text-gray-700 mb-3">
							호차 설정
						</h3>
						<div className="space-y-3 text-xs">
							<div>
								<label className="block text-gray-500 mb-1">라벨</label>
								<input
									type="text"
									value={selectedCar?.label || ""}
									onChange={(event) =>
										updateSelectedCar((car) => ({
											...car,
											label: event.target.value,
										}))
									}
									className="w-full border border-gray-300 rounded px-2 py-1"
								/>
							</div>
							<div>
								<label className="block text-gray-500 mb-1">이미지 URL</label>
								<input
									type="text"
									value={selectedCar?.image_url || ""}
									onChange={(event) =>
										updateSelectedCar((car) => ({
											...car,
											image_url: event.target.value,
										}))
									}
									className="w-full border border-gray-300 rounded px-2 py-1"
								/>
							</div>
							<div>
								<label className="block text-gray-500 mb-1">이미지 경로</label>
								<input
									type="text"
									value={selectedCar?.image_path || ""}
									onChange={(event) =>
										updateSelectedCar((car) => ({
											...car,
											image_path: event.target.value,
										}))
									}
									className="w-full border border-gray-300 rounded px-2 py-1"
								/>
							</div>
						</div>
					</div>

					<div className="bg-white shadow rounded-lg p-4">
						<h3 className="text-sm font-semibold text-gray-700 mb-3">
							선택 객체
						</h3>
						{selectedObject ? (
							<div className="space-y-3 text-xs">
								<div>
									<label className="block text-gray-500 mb-1">타입</label>
									<select
										value={selectedObject.object_type}
										onChange={(event) =>
											updateSelectedObject({ object_type: event.target.value })
										}
										className="w-full border border-gray-300 rounded px-2 py-1"
									>
										{TOOL_OPTIONS.filter(
											(tool) => !["select", "erase", "table_between", "partition_between"].includes(tool.id)
										).map((tool) => (
											<option key={tool.id} value={tool.id}>
												{tool.label}
											</option>
										))}
									</select>
								</div>
								<div>
									<label className="block text-gray-500 mb-1">Object ID</label>
									<input
										type="text"
										value={selectedObject.object_id || ""}
										onChange={(event) =>
											updateSelectedObject({ object_id: event.target.value })
										}
										className="w-full border border-gray-300 rounded px-2 py-1"
									/>
								</div>
								{selectedObject.object_type === "seat" && (
									<div>
										<label className="block text-gray-500 mb-1">Seat Code</label>
										<input
											type="text"
											value={selectedObject.seat_code || ""}
											readOnly={shouldAutoComputeSeatCode(numbering)}
											onChange={(event) =>
												updateSelectedObject({ seat_code: event.target.value })
											}
											className={`w-full border border-gray-300 rounded px-2 py-1 ${
												shouldAutoComputeSeatCode(numbering) ? "bg-gray-50" : ""
											}`}
										/>
										{!shouldAutoComputeSeatCode(numbering) && (
											<p className="mt-1 text-[10px] text-gray-400">
												숫자 좌석번호는 수동으로 수정할 수 있습니다.
											</p>
										)}
									</div>
								)}
								<div className="grid grid-cols-2 gap-2">
									<div>
										<label className="block text-gray-500 mb-1">Row</label>
										<input
											type="number"
											value={selectedObject.row ?? ""}
											onChange={(event) =>
												updateSelectedObject({
													row: event.target.value ? parseInt(event.target.value, 10) : null,
												})
											}
											className="w-full border border-gray-300 rounded px-2 py-1"
										/>
									</div>
									<div>
										<label className="block text-gray-500 mb-1">Col</label>
										<input
											type="text"
											value={selectedObject.col ?? ""}
											onChange={(event) =>
												updateSelectedObject({ col: event.target.value || null })
											}
											className="w-full border border-gray-300 rounded px-2 py-1"
										/>
									</div>
								</div>
								<div className="grid grid-cols-2 gap-2">
									<div>
										<label className="block text-gray-500 mb-1">Row Span</label>
										<input
											type="number"
											value={selectedObject.row_span || 1}
											onChange={(event) =>
												updateSelectedObject({
													row_span: parseInt(event.target.value, 10) || 1,
												})
											}
											className="w-full border border-gray-300 rounded px-2 py-1"
										/>
									</div>
									<div>
										<label className="block text-gray-500 mb-1">Col Span</label>
										<input
											type="number"
											value={selectedObject.col_span || 1}
											onChange={(event) =>
												updateSelectedObject({
													col_span: parseInt(event.target.value, 10) || 1,
												})
											}
											className="w-full border border-gray-300 rounded px-2 py-1"
										/>
									</div>
								</div>
								<div>
									<label className="block text-gray-500 mb-1">Direction</label>
									<select
										value={selectedObject.direction || ""}
										onChange={(event) =>
											handleSeatDirectionChange(event.target.value || "")
										}
										className="w-full border border-gray-300 rounded px-2 py-1"
									>
										<option value="">없음</option>
										{DIRECTION_ORDER.map((dir) => (
											<option key={dir} value={dir}>
												{DIRECTION_LABELS[dir]}
											</option>
										))}
									</select>
								</div>
								<div>
									<label className="block text-gray-500 mb-1">Position Hint</label>
									<input
										type="text"
										value={selectedObject.position_hint || ""}
										onChange={(event) =>
											updateSelectedObject({
												position_hint: event.target.value || null,
											})
										}
										className="w-full border border-gray-300 rounded px-2 py-1"
									/>
								</div>
								<div>
									<label className="block text-gray-500 mb-1">Notes</label>
									<textarea
										value={selectedObject.notes || ""}
										onChange={(event) =>
											updateSelectedObject({ notes: event.target.value || null })
										}
										className="w-full border border-gray-300 rounded px-2 py-1"
										rows={2}
									/>
								</div>
								<div className="flex gap-2">
									<button
										type="button"
										onClick={() =>
											handleSeatDirectionChange(
												rotateDirection(selectedObject.direction || seatDirection)
											)
										}
										className="flex-1 px-3 py-2 text-xs bg-gray-100 rounded-lg hover:bg-gray-200"
									>
										방향 회전
									</button>
									<button
										type="button"
										onClick={() => {
											if (!selectedObject) return;
											if (selectedObject.row && selectedObject.col) {
												removeObjectAt(selectedObject.row, selectedObject.col);
											} else {
												updateSelectedCar((car) => ({
													...car,
													objects: (car.objects || []).filter(
														(obj) => obj.__editorId !== selectedObject.__editorId
													),
												}));
												setSelectedObjectId(null);
											}
										}}
										className="flex-1 px-3 py-2 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
									>
										삭제
									</button>
								</div>
							</div>
						) : (
							<p className="text-xs text-gray-500">선택된 객체가 없습니다.</p>
						)}
					</div>
				</div>
			</div>

			{isLayoutModalOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
						<div
							className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
							onClick={() => setIsLayoutModalOpen(false)}
						/>

						<div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
							<h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
								새 레이아웃 추가
							</h3>
							<form onSubmit={handleCreateLayout} className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										열차종 <span className="text-red-500">*</span>
									</label>
									<select
										value={layoutForm.train_type_code}
										onChange={(event) =>
											setLayoutForm({
												...layoutForm,
												train_type_code: event.target.value,
											})
										}
										className="w-full border border-gray-300 rounded-lg px-3 py-2"
										required
									>
										<option value="">선택</option>
										{trainTypes.map((type) => (
											<option key={type.code} value={type.code}>
												{type.name} ({type.code}) · {type.category === "bus" ? "버스" : "열차"}
											</option>
										))}
									</select>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										레이아웃 이름 <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										value={layoutForm.name}
										onChange={(event) =>
											setLayoutForm({ ...layoutForm, name: event.target.value })
										}
										className="w-full border border-gray-300 rounded-lg px-3 py-2"
										required
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Variant
									</label>
									<input
										type="text"
										value={layoutForm.variant}
										onChange={(event) =>
											setLayoutForm({ ...layoutForm, variant: event.target.value })
										}
										className="w-full border border-gray-300 rounded-lg px-3 py-2"
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Source URL
									</label>
									<input
										type="text"
										value={layoutForm.source_url}
										onChange={(event) =>
											setLayoutForm({ ...layoutForm, source_url: event.target.value })
										}
										className="w-full border border-gray-300 rounded-lg px-3 py-2"
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Snapshot Path
									</label>
									<input
										type="text"
										value={layoutForm.source_snapshot_path}
										onChange={(event) =>
											setLayoutForm({
												...layoutForm,
												source_snapshot_path: event.target.value,
											})
										}
										className="w-full border border-gray-300 rounded-lg px-3 py-2"
									/>
								</div>
								<div className="flex justify-end gap-2 pt-4">
									<button
										type="button"
										onClick={() => setIsLayoutModalOpen(false)}
										className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
									>
										취소
									</button>
									<button
										type="submit"
										disabled={isLoading}
										className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
									>
										{isLoading ? "처리 중..." : "생성"}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}

			{isCarModalOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
						<div
							className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
							onClick={() => setIsCarModalOpen(false)}
						/>

						<div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
							<h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
								호차 추가
							</h3>
							<form onSubmit={handleAddCar} className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										호차 번호 <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										value={carForm.car_number}
										onChange={(event) =>
											setCarForm({ ...carForm, car_number: event.target.value })
										}
										className="w-full border border-gray-300 rounded-lg px-3 py-2"
										required
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										라벨
									</label>
									<input
										type="text"
										value={carForm.label}
										onChange={(event) =>
											setCarForm({ ...carForm, label: event.target.value })
										}
										className="w-full border border-gray-300 rounded-lg px-3 py-2"
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										이미지 URL
									</label>
									<input
										type="text"
										value={carForm.image_url}
										onChange={(event) =>
											setCarForm({ ...carForm, image_url: event.target.value })
										}
										className="w-full border border-gray-300 rounded-lg px-3 py-2"
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										이미지 경로
									</label>
									<input
										type="text"
										value={carForm.image_path}
										onChange={(event) =>
											setCarForm({ ...carForm, image_path: event.target.value })
										}
										className="w-full border border-gray-300 rounded-lg px-3 py-2"
									/>
								</div>
								<div className="flex justify-end gap-2 pt-4">
									<button
										type="button"
										onClick={() => setIsCarModalOpen(false)}
										className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
									>
										취소
									</button>
									<button
										type="submit"
										className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
									>
										추가
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
